"use client";

import { getBaseApiClient } from "@newxsystems/api-base/client";
import { getBlogPostsPageQueryKey } from "@newxsystems/api-base/keys";
import { cn } from "@newxsystems/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { parseAsInteger, useQueryState } from "nuqs";
import { useEffect, useMemo, useRef, useState } from "react";
import { KaajBlogPostCard } from "../../../../modules/blog/components/KaajBlogPostCard/KaajBlogPostCard";
import { KaajSectionShell } from "../../../../modules/layout/components/KaajSectionShell/KaajSectionShell";
import type { IBlogListingBlockProps } from "./BlogListingBlock.props";

const getPaginationItems = (currentPage: number, totalPages: number) =>
  Array.from(new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]))
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

const paginationButtonClass =
  "inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-full border border-[#D9E1FF] bg-white px-4 text-sm font-bold text-[#151927] transition-colors hover:border-[#2144FF] hover:text-[#2144FF] focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#2144FF] disabled:cursor-not-allowed disabled:opacity-50";

export const BlogListingBlockClient = ({
  anchorId,
  emptyText,
  eyebrow,
  heading,
  pageSize,
  spacing,
  subheading,
}: IBlogListingBlockProps) => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [pendingPage, setPendingPage] = useState<number | null>(null);
  const [pageQuery, setPageQuery] = useQueryState(
    "page",
    parseAsInteger.withDefault(1).withOptions({
      clearOnDefault: true,
      history: "push",
      scroll: false,
      shallow: true,
    }),
  );
  const currentPage = Math.max(pageQuery, 1);
  const { data, isFetching, isPlaceholderData } = useQuery({
    queryKey: getBlogPostsPageQueryKey(currentPage, pageSize),
    queryFn: async () => {
      const client = getBaseApiClient();
      if (!client) throw new Error("Base API client is not available.");
      return client.blog.list({ page: currentPage, pageSize });
    },
    placeholderData: keepPreviousData,
  });
  const posts = data?.items ?? [];
  const featuredPosts = (data?.page ?? currentPage) === 1 ? (data?.featuredPosts ?? []) : [];
  const totalPages = data?.totalPages ?? 1;
  const paginationItems = useMemo(
    () => getPaginationItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  useEffect(() => {
    if (pendingPage == null || isFetching || isPlaceholderData || data?.page !== pendingPage)
      return;
    setPendingPage(null);
    window.requestAnimationFrame(() =>
      sectionRef.current?.scrollIntoView({ behavior: "auto", block: "start" }),
    );
  }, [data?.page, isFetching, isPlaceholderData, pendingPage]);

  const selectPage = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage || isFetching) return;
    setPendingPage(page);
    void setPageQuery(page);
  };

  const [leadFeatured, ...secondaryFeatured] = featuredPosts.slice(0, 3);
  const hasIntro = Boolean(eyebrow || heading || subheading);

  return (
    <KaajSectionShell
      id={anchorId ?? undefined}
      containerClassName="scroll-mt-6"
      spacing={spacing ?? "normal"}
    >
      <div ref={sectionRef} />
      {hasIntro ? (
        <header className="mb-10 max-w-4xl md:mb-14">
          {eyebrow ? <p className="mb-4 text-sm font-bold text-[#2144FF]">{eyebrow}</p> : null}
          {heading ? (
            <h2 className="kaaj-section-heading max-w-3xl text-[#151927]">{heading}</h2>
          ) : null}
          {subheading ? (
            <div
              className="mt-5 max-w-2xl text-pretty text-base leading-7 text-[#4B5568] md:text-lg"
              dangerouslySetInnerHTML={{ __html: subheading }}
            />
          ) : null}
        </header>
      ) : null}

      {leadFeatured ? (
        <div className="mb-10 lg:relative lg:left-1/2 lg:w-screen lg:-translate-x-1/2 lg:px-6 xl:px-8">
          <div className="mx-auto w-full lg:max-w-[88rem] xl:max-w-[98rem]">
            <div
              className={cn(
                "grid gap-5 lg:gap-6",
                secondaryFeatured.length > 0 &&
                  "lg:grid-cols-[minmax(0,1.55fr)_minmax(19rem,0.85fr)] lg:grid-rows-2",
              )}
            >
              <KaajBlogPostCard
                className={cn(
                  "min-h-[28rem] lg:min-h-[40rem]",
                  secondaryFeatured.length > 0 && "lg:row-span-2",
                )}
                post={leadFeatured}
                priority
                variant="featured"
              />
              {secondaryFeatured.map((post) => (
                <KaajBlogPostCard
                  className="min-h-[22rem]"
                  key={post.id}
                  post={post}
                  variant="featuredCompact"
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {posts.length ? (
        <ul
          aria-busy={isFetching}
          className="grid list-none gap-5 p-0 md:grid-cols-2 lg:gap-6 xl:grid-cols-3"
        >
          {posts.map((post, index) => (
            <li className="min-w-0" key={post.id}>
              <KaajBlogPostCard post={post} priority={index < 3 && currentPage === 1} />
            </li>
          ))}
        </ul>
      ) : !leadFeatured && emptyText ? (
        <div className="rounded-[1.5rem] border border-dashed border-[#AEBEFF] bg-white p-6 text-[#4B5568]">
          {emptyText}
        </div>
      ) : null}

      {totalPages > 1 ? (
        <nav
          aria-label="Blog posts pagination"
          className="mt-10 flex flex-wrap items-center justify-center gap-2 lg:mt-12"
        >
          <button
            className={paginationButtonClass}
            disabled={currentPage <= 1 || isFetching}
            onClick={() => selectPage(currentPage - 1)}
            type="button"
          >
            Previous
          </button>
          {paginationItems.map((pageNumber, index) => {
            const showGap = index > 0 && pageNumber - paginationItems[index - 1]! > 1;
            const isCurrent = pageNumber === currentPage;
            return (
              <span className="inline-flex items-center gap-2" key={pageNumber}>
                {showGap ? (
                  <span aria-hidden="true" className="px-1 text-[#4B5568]">
                    …
                  </span>
                ) : null}
                <button
                  aria-current={isCurrent ? "page" : undefined}
                  aria-label={`Page ${pageNumber}${isCurrent ? ", current page" : ""}`}
                  className={cn(
                    paginationButtonClass,
                    isCurrent && "border-[#2144FF] bg-[#2144FF] text-white hover:text-white",
                  )}
                  disabled={isCurrent || isFetching}
                  onClick={() => selectPage(pageNumber)}
                  type="button"
                >
                  {pendingPage === pageNumber && isFetching ? (
                    <span className="sr-only">Loading </span>
                  ) : null}
                  {pageNumber}
                </button>
              </span>
            );
          })}
          <button
            className={paginationButtonClass}
            disabled={currentPage >= totalPages || isFetching}
            onClick={() => selectPage(currentPage + 1)}
            type="button"
          >
            Next
          </button>
        </nav>
      ) : null}
    </KaajSectionShell>
  );
};

BlogListingBlockClient.displayName = "Block::BlogListingBlockClient";
